import { useState } from 'react';
import { useT } from '@hana/i18n';
import { Button, Input, Modal, useToast } from '@hana/ui';
import type { InquiryAgent, InquiryQuote } from './types';
import { usd } from './format';
import './AgentModal.i18n';
import s from './inquiry.module.css';

export type AgentModalKind = 'ai' | 'price' | 'thread' | 'win';
export interface AgentModalState { kind: AgentModalKind; agent: InquiryAgent }

/** Hộp thoại thao tác theo đối tác ở chi tiết yêu cầu (board Mint-RateInquiry, `md.kind` ai · price · thread · win). Ghi chờ bật DB. */
export function AgentModal({ state, inquiry, created, subject, quote, onClose }: {
  state: AgentModalState | null;
  inquiry: string;
  created: string;
  subject: string;
  quote?: InquiryQuote;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [off, setOff] = useState<ReadonlySet<string>>(new Set());
  const pending = () => toast({ msg: t('am.noWrite'), tone: 'err' });
  if (!state) return null;
  const { kind, agent: a } = state;
  const [carrier = '—', transit = '—', valid = '—'] = a.info.split(' · ');
  const Check = ({ k, v }: { k: string; v: string }) => {
    const on = !off.has(k);
    return (
      <label className={s.mdRow}>
        <input type="checkbox" checked={on} aria-label={t('am.use', { k })} onChange={() => setOff((p) => { const n = new Set(p); if (!n.delete(k)) n.add(k); return n; })} />
        <span className={s.mdKey}>{k}</span>
        <span className={s.mdVal}>{v}</span>
      </label>
    );
  };
  const close = () => { setOff(new Set()); onClose(); };

  let title = '';
  let lead = '';
  let body: React.ReactNode = null;
  let foot: React.ReactNode = null;
  if (kind === 'ai') {
    title = t('am.aiTitle', { name: a.name });
    lead = t('am.aiLead');
    body = (
      <>
        <Check k={t('am.carrier')} v={carrier} />
        <Check k={t('am.orig')} v={quote?.orig ?? '—'} />
        <Check k={t('am.usd')} v={quote ? usd(quote.usd) : '—'} />
        <Check k="Transit" v={transit} />
        <Check k={t('am.valid')} v={valid} />
      </>
    );
    foot = (<><Button onClick={close}>{t('am.cancel')}</Button><Button variant="primary" onClick={pending}>{t('am.save')}</Button></>);
  } else if (kind === 'price') {
    title = t('am.priceTitle', { name: a.name });
    lead = t('am.priceLead');
    body = (<>{([['carrier', 'am.carrier'], ['cur', 'am.cur'], ['allin', 'am.allin'], ['transit', 'am.transit'], ['until', 'am.until']] as const).map(([k, l]) => <Input key={k} label={t(l)} defaultValue={k === 'cur' ? 'USD' : ''} />)}</>);
    foot = (<><Button onClick={close}>{t('am.cancel')}</Button><Button variant="primary" onClick={pending}>{t('am.save')}</Button></>);
  } else if (kind === 'thread') {
    title = t('am.threadTitle', { name: a.name });
    lead = t('am.threadLead');
    const reply = a.status === 'n' ? 'Sorry, we cannot offer this lane at the moment.' : `Please find our best offer: ${a.info}${quote ? ` · ${quote.orig}` : ''}`;
    body = (
      <>
        {[['pricing@ (HANA)', `${created} 12:31`, subject], [a.name, a.when.split(' · ')[0] ?? '', reply]].map(([who, at, k], i) => (
          <div key={who} className={i ? s.mdMsgIn : s.mdMsgOut}><div className={s.sub}>{who} · {at}</div>{k}</div>
        ))}
      </>
    );
    foot = (<><Button onClick={pending}>{t('am.fwd')}</Button><Button variant="primary" onClick={pending}>{t('am.reply')}</Button></>);
  } else {
    title = t('am.winTitle', { name: a.name, inq: inquiry });
    lead = t('am.winLead');
    foot = (<><Button onClick={close}>{t('am.cancel')}</Button><Button variant="primary" onClick={pending}>{t('am.pick')}</Button></>);
  }
  return (
    <Modal open onOpenChange={(o) => !o && close()} title={title} description={lead} footer={<div className={s.mdFoot}>{foot}</div>}>
      {body && <div className={s.mdBody}>{body}</div>}
    </Modal>
  );
}
